import { motion } from "framer-motion";
import { Link } from "react-router-dom";
import { ArrowUpRight } from "lucide-react";
import { useLang } from "../lib/LanguageContext";
import { featuredItems } from "../lib/menuData";

export const FeaturedMenu = () => {
  const { t, lang } = useLang();

  return (
    <section
      id="menu"
      data-testid="featured-menu-section"
      className="relative py-24 md:py-32 bg-brand-cream"
    >
      <div className="mx-auto max-w-7xl px-6 md:px-10 lg:px-12">
        <div className="flex flex-col md:flex-row md:items-end md:justify-between gap-6">
          <div className="max-w-2xl">
            <span className="text-xs uppercase tracking-[0.32em] text-brand-blue font-medium">
              {t.featured.eyebrow}
            </span>
            <h2 className="mt-5 font-serif text-4xl sm:text-5xl lg:text-[3.4rem] leading-[1.05] tracking-tight text-brand-ink">
              {t.featured.title}
            </h2>
            <p className="mt-5 text-base md:text-lg text-brand-mocha leading-relaxed max-w-xl">
              {t.featured.subtitle}
            </p>
          </div>
          <Link
            to="/menu"
            data-testid="featured-see-all"
            className="group hidden md:inline-flex items-center gap-2 text-sm font-medium text-brand-ink border-b border-brand-ink pb-1 hover:text-brand-blue hover:border-brand-blue transition-colors"
          >
            {t.featured.cta}
            <ArrowUpRight
              className="w-4 h-4 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5"
              strokeWidth={1.8}
            />
          </Link>
        </div>

        <div className="mt-14 grid sm:grid-cols-2 lg:grid-cols-3 gap-6 md:gap-7">
          {featuredItems.map((item, i) => (
            <motion.article
              key={item.name}
              initial={{ opacity: 0, y: 28 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true, margin: "-80px" }}
              transition={{ duration: 0.55, delay: (i % 3) * 0.08 }}
              data-testid={`featured-card-${i}`}
              className="group bg-white rounded-3xl overflow-hidden border border-brand-border transition-all hover:-translate-y-1 hover:shadow-xl hover:shadow-brand-coffee/10 hover:border-brand-blue/30"
            >
              <div className="relative aspect-[4/3] overflow-hidden bg-brand-sand">
                <img
                  src={item.image}
                  alt={item.name}
                  loading="lazy"
                  className="w-full h-full object-cover transition-transform duration-700 group-hover:scale-105"
                />
                <span className="absolute top-4 left-4 bg-white/90 backdrop-blur-md px-3 py-1 rounded-full text-[10px] uppercase tracking-[0.2em] font-medium text-brand-blue">
                  {item.category}
                </span>
              </div>
              <div className="p-6">
                <div className="flex items-start justify-between gap-3">
                  <h3 className="font-serif text-xl md:text-2xl leading-tight tracking-tight text-brand-ink">
                    {item.name}
                  </h3>
                  <span className="font-serif text-lg md:text-xl text-brand-blue whitespace-nowrap">
                    Rp {item.price}k
                  </span>
                </div>
                <p className="mt-3 text-sm text-brand-mocha leading-relaxed">
                  {lang === "id" ? item.desc_id : item.desc_en}
                </p>
              </div>
            </motion.article>
          ))}
        </div>

        <div className="mt-12 md:hidden">
          <Link
            to="/menu"
            className="inline-flex items-center gap-2 text-sm font-medium text-brand-ink border-b border-brand-ink pb-1"
          >
            {t.featured.cta}
            <ArrowUpRight className="w-4 h-4" strokeWidth={1.8} />
          </Link>
        </div>
      </div>
    </section>
  );
};
